import MidiEffect from '../MidiEffect.js';
import PlaneStats from './PlaneStats.js';
import {WIDTH, HEIGHT, PADDING} from 'components/effects/plane/constants.js';
import {centroid} from "effects/midi/mappable/lang/geometry.js"
import PlaneGroup from './PlaneGroup.js';
import Drawable from './Drawable.js';
import Regions from './Regions.js';
import {PARAMS_TO_NOT_MODULATE, PARAMS_TO_LOWER_DEPTH} from './getTrackParameters.js';
import ParamMappable from './ParamMappable.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Snapshotting from "effects/midi/mappable/Snapshotting.js";
import PlaneMacroable from './PlaneMacroable.js';
import Playable from "effects/midi/mappable/Playable.js";
import {setEffectParameterValues} from 'actions/index.js';
import {compose} from "utils/compose.js"

// Probability that we connect an auto-mapped lfo to the lfo

const LFO_PROBABILITY = 35;

const MAX_PARAMETERS_PER_ITER = 24;
    
class PlaneParametersEffect extends compose(
    Regions,
    PlaneStats,
    ParamMappable,
    Snapshotting,
    Playable,
    PlaneMacroable,
    Drawable)
(MidiEffect) {
    constructor(track) {
        super('Plane Parameters', track);

        this.setupRampParameters();
        this.setupSnapshots();
        this.setupDrawable();
        this.setupPlayable();
        this.groups = [];

        this.counter = -1;
   }

    randomlyMapParameters(device, num, parameters) {
        parameters = super.randomlyMapParameters(device, num, parameters);

        // connect each to a lfo and set depth to random number
        for (let p of parameters) {
            if (p.lfos.length === 0) {
                let lfos = this.track.sequencerEngine.lfos.map(x => x.lfo);
                if (p.context.lfoTrack) {
                    p.context.lfoTrack.lfo.isSynced = false;
                } else if (p.context.midiEffect) {
                } else if (p.isConnectedToAnyModulator()) {
                    // if its already connected to a modulator dont fuck with it
                } else if (Math.random() < LFO_PROBABILITY / 100.0) {
                    if (!PARAMS_TO_NOT_MODULATE.includes(p.name)) {
                        let lfo = choose(lfos);
                        p.connectToLFO(lfo);
                        p.depth = 0;
                    }
                } else {
                    p.depth = 0;
                }
            }
        }
    }

    stop() {
        super.stop();
        this.counter = -1;
        this.stopDrawing();
        this.groups.forEach(g => g.stop());
    }

    clear() {
        super.clear();
        this.counter = -1;
    }

    output(time, stepNumber, _stepNumber) {
        this.currentSnapshotParameter.refresh(false);
        if (!this.track.sequencerEngine.playing) {
            return this.triggers;
        }

        if (stepNumber === 0 && this.resetAtZero && this.counter !== 0) {
            this.stop();
            this._clear();
        }

        let totalParameters = this.groups.length > 0 ? this.groups.map(x => x._parameters.length)
                .reduce((a,b) => a + b) :
                1;
            
        // limit the # of parameters used per group 
        let ratio = Math.min(1, MAX_PARAMETERS_PER_ITER / totalParameters);

        this.applyPencilAutomations(stepNumber);
 
        if (this.currentPath) {
            let bulk = this.applyPath(ratio, stepNumber);

            this.track.sequencerEngine.parametersToUpdate = {
                ... this.track.sequencerEngine.parametersToUpdate,
                ... bulk
            };
            return this.triggers;
        }

        if (this.triggers.length === 0) {
            return this.triggers;
        }

        if (!this.playing) {
            return this.triggers;
        }

        let bulk = {};
       
        for (let group of this.groups) {
            bulk = {
                ... bulk,
                ... group.onTrigger(_stepNumber, time, this.rampTime, ratio)
            };
        }
        this.track.sequencerEngine.parametersToUpdate = {
            ... this.track.sequencerEngine.parametersToUpdate,
            ... bulk
        };
        return this.triggers;
    };

    getParameters() {
        let parameters = super.getParameters();
        for (let group of this.groups) {
            parameters = [... parameters, ... group.parameters];
        }
        return parameters;
    }

    getParameterValueToSet(i) {
        // returns the value of the parameter at index i at this time
    }

    groupParameters(parameters) {
        let group = new PlaneGroup(parameters, undefined, this.context, this.track, this);
        this.groups = [... this.groups, group];
        parameters.forEach(p => p.hasGroup = group);
    }

    ungroup(group) {
        let i = this.groups.indexOf(group);
        group._parameters.forEach(
            p => p.hasGroup = undefined);
        this.groups.splice(i, 1);
    }

    ungroupParameters(parameters) {
        parameters
            .filter(p => p.hasGroup)
            .forEach(
            p => p.hasGroup.ungroupParameter(p));
    }

    getGroupForParameter(parameter) {
        return this.groups.find(
            g => g._parameters.includes(parameter));
    }

    mergeIntoGroup(group, parameters) {
        // first ungroup all these parameters
        this.ungroupParameters(parameters);
        parameters.forEach(p => {
            group._parameters.push(p);
            p.hasGroup = group;
        });

        let toDelete = this.groups.filter(
            g => g._parameters.length === 0);
        this.groups = this.groups.filter(
            g => !toDelete.includes(g));
    }

    getCopy(track, ignoreId=true) {
        let effect = new PlaneParametersEffect(track);
        effect.fromJson(this.getJson(), ignoreId);
        return effect;
    }


    getJson(isPreset) {
        return {
            ... super.getJson(isPreset),
            densities: this.getDensityJson(),
            scriptExpanded: this.scriptExpanded && this.scriptExpanded.id,
            macros: this.getMacrosJson(),
            snapshots: this.snapshots.map(
                snap => snap.map(
                ({parameter, coordinates}) => ({
                    parameter: parameter.id,
                    coordinates
                }))),
            snapshotComponent: this.snapshotComponent,
            groups: this.groups.map(g => g.getJson())
        };
    }

    unmapParameter(i) {
        let parameter = super.unmapParameter(i);
        if (parameter && parameter.hasGroup) {
            parameter.hasGroup.ungroupParameter(parameter);
        }
        if (parameter && parameter.planeMacro) {
            let macro = parameter.planeMacro;
            macro.removeParameter(parameter);
            if (macro.isEmpty()) {
                this.deleteMacro(macro);
            }
        }
    }

    fromJson(json, isCopy=false) {
        if (!isCopy) {
            this.id = json.id;
        }
        this.jsonSnapshots = json.snapshots;
        
        if (json.snapshotComponent) {
            this.snapshotComponent = json.snapshotComponent;
        }

        if (json.groups) {
            for (let _group of json.groups) {
                let group = new PlaneGroup(undefined, undefined, this.context, this.track, this);
                group.fromJson(_group);
                this.groups.push(group);
            }
        }
        this.fromMacrosJson(json.macros);

        if (json.scriptExpanded) {
            this.scriptExpanded = this.groups.find(
                ({id}) => id === json.scriptExpanded);
        }

        if (json.densities) {
            this.fromDensityJson(json.densities);
        }

        return super.fromJson(json, isCopy);
    }

    loadParameters(parameterIds, initialLoad) {
        let {lostParameters, newMappings} = super.loadParameters(parameterIds, initialLoad);
        this.groups.forEach(
            g => g.loadParameters(this._parameters, newMappings));
        this.macros.forEach(
            m => m.loadParameters(this._parameters, newMappings));

        this.snapshots = [];
        if (this.jsonSnapshots) {
            for (let snapshot of this.jsonSnapshots) {
                let _snapshot = [];
                for (let {parameter, coordinates} of snapshot) {
                    let _parameter = this._parameters.find(p => p.id === parameter);
                    if (_parameter) {
                        _snapshot.push({parameter: _parameter, coordinates});
                    }
                }
                this.snapshots.push(_snapshot);
            }
        }

        // we call stop in order to reset all the values 
        this.clear();
        this.stop();
        return {lostParameters, newMappings};
    }

        
    clearParameter(parameter, i, skipGroup) {
        super.clearParameter(parameter, i);

        if (!skipGroup) {
            this.groups.forEach(
                g => g.updateParameter(parameter));
        }
        this.currentSnapshot = undefined;
    }
        
    replaceParameter(parameter) {
        super.replaceParameter(parameter);
        this.groups.forEach(g => g.replaceParameter(parameter));
    }

    dispose() {
        this.macros.forEach(m => m.dispose());
        super.dispose();
    }

    sortParameters() {}
}

const choose = (x) => {
    return x[Math.floor(Math.random() * x.length)];
};

export default PlaneParametersEffect;


